import React, { useState } from 'react';

interface LeadCaptureFormProps {
  type?: 'popup' | 'inline';
  incentive?: string;
  onClose?: () => void;
  onSubmit?: (data: FormData) => void;
}

interface FormData {
  name: string;
  email: string;
  projectDetails: string;
}

const LeadCaptureForm: React.FC<LeadCaptureFormProps> = ({ 
  type = 'inline', 
  incentive = "Get Your Free Quote + 10% Off Your First Project",
  onClose,
  onSubmit 
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    projectDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    
    if (onSubmit) {
      onSubmit(formData);
    }
    
    // Reset form after success
    setTimeout(() => {
      setSubmitSuccess(false);
      setFormData({
        name: '',
        email: '',
        projectDetails: ''
      });
      if (onClose) {
        onClose();
      }
    }, 3000);
  };

  if (type === 'popup') {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-amber-600/30 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-600/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-600/5 rounded-full translate-y-12 -translate-x-12"></div>
          
          <button
            onClick={onClose}
            className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors z-10"
            aria-label="Close form"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="text-center mb-8 relative z-10">
            <div className="text-6xl mb-6">🎁</div>
            <h3 className="text-3xl font-bold text-amber-400 mb-4">Limited Time Offer!</h3>
            <p className="text-gray-200 text-lg font-medium">{incentive}</p>
          </div>

          {submitSuccess ? (
            <div className="text-center relative z-10">
              <div className="text-6xl mb-6">✅</div>
              <h3 className="text-2xl font-bold text-green-400 mb-4">Thank You!</h3>
              <p className="text-green-300 text-lg">
                We'll contact you within 24 hours with your free quote and special offer.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              <div>
                <label htmlFor="name" className="block text-amber-400 font-semibold mb-3 text-base">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-amber-400 font-semibold mb-3 text-base">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <div>
                <label htmlFor="projectDetails" className="block text-amber-400 font-semibold mb-3 text-base">
                  Project Details
                </label>
                <textarea
                  id="projectDetails"
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 disabled:bg-gray-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600 disabled:cursor-not-allowed text-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Sending...
                  </div>
                ) : (
                  "Get Free Quote + Special Offer"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Inline form
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-2xl border border-amber-600/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-amber-600/10 rounded-full -translate-y-12 translate-x-12"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-amber-600/5 rounded-full translate-y-8 -translate-x-8"></div>
      
      <div className="text-center mb-8 relative z-10">
        <div className="text-4xl mb-4">🎁</div>
        <h3 className="text-2xl font-bold text-amber-400 mb-3">Get Your Free Quote</h3>
        <p className="text-gray-200 text-base">{incentive}</p>
      </div>

      {submitSuccess ? (
        <div className="text-center relative z-10">
          <div className="text-5xl mb-6">✅</div>
          <h3 className="text-xl font-bold text-green-400 mb-4">Thank You!</h3>
          <p className="text-green-300 text-base">
            We'll contact you within 24 hours with your free quote and special offer.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
          <div>
            <label htmlFor="name" className="block text-amber-400 font-semibold mb-3 text-base">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base"
              placeholder="Your full name"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-amber-400 font-semibold mb-3 text-base">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base"
              placeholder="your.email@example.com"
            />
          </div>
          
          <div>
            <label htmlFor="projectDetails" className="block text-amber-400 font-semibold mb-3 text-base">
              Project Details
            </label>
            <textarea
              id="projectDetails"
              name="projectDetails"
              value={formData.projectDetails}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-base resize-none"
              placeholder="Tell us about your project, timeline, and any specific requirements..."
            />
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 disabled:bg-gray-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-xl border border-amber-600 disabled:cursor-not-allowed text-lg"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                Sending...
              </div>
            ) : (
              "Get Free Quote + Special Offer"
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default LeadCaptureForm; 